import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase.js';
import { Helmet } from 'react-helmet-async';
import GoogleAnalytics from '../../analyticsComponents/GoogleAnalytics.js';
import { CreateTable } from '../SubjectPage/table/table';
import toPascalCase from '../../utils/toPascalCase';
import Spinner from '../../utils/Spinner';
import logger from '../../utils/logger';

function GradePage() {
  const location = useLocation();
  const { gradeName, subjects } = location.state || { gradeName: '', subjects: [] };
  const [cleanGrade, setCleanGrade] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [subjectSearch, setSubjectSearch] = useState('');

  const collectionOne = 'PDFS';
  const headerRef = useRef(null);

  // Filter subjects by search term (for grades with many subjects)
  const filteredSubjects = useMemo(() => {
    if (!subjectSearch.trim()) return subjects;
    return subjects.filter(s => s.toLowerCase().includes(subjectSearch.toLowerCase()));
  }, [subjects, subjectSearch]);

  // Ensure the page starts at the top (heading in view) when navigating here
  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, []);

  function trimGradeSpace(grade) {
    const cleanGrade = grade.trim().replace(/\s+/g, '');
    return cleanGrade;
  }

  useEffect(() => {
    if (!selectedSubject) return;

    const subjectId = toPascalCase(selectedSubject);
    const cleanGrade = trimGradeSpace(gradeName);
    setCleanGrade(cleanGrade);
    const mainDoc = cleanGrade;
    const CollectionTwo = subjectId;

    setLoading(true);
    setError('');

    try {
      const resourceRef = collection(db, collectionOne, mainDoc, CollectionTwo);
      const q = query(resourceRef, where('verified', '==', true));
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const docs = snapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }));
          setResources(docs);
          setLoading(false);
        },
        (err) => {
          logger.error('Firestore subscription error:', err);
          setError('Failed to load resources.');
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      logger.error(err);
      setError('Failed to fetch resources.');
      setLoading(false);
    }
  }, [selectedSubject, gradeName]);

  const hasGrade = Boolean(gradeName);

  return (
    <div className="grade-page w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <GoogleAnalytics pages="GradePage" eventType="grade_page" />

      <Helmet>
        <title>{gradeName} | Share2Teach Subjects</title>
        <meta name="description" content={`Explore subjects for ${gradeName} on Share2Teach.`} />
      </Helmet>

      {/* Breadcrumb + title row */}
      <div ref={headerRef} className="mb-6 scroll-mt-header">
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
          <Link to="/" className="hover:text-brand-600 transition-colors">Home</Link>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
          <span className="text-gray-600 font-medium">{gradeName || 'Grade'}</span>
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              {hasGrade ? gradeName : 'Browse Resources'}
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">
              {subjects.length} subject{subjects.length !== 1 ? 's' : ''} available
              {selectedSubject && <> · Viewing <span className="font-medium text-gray-700">{selectedSubject}</span></>}
            </p>
          </div>
          {selectedSubject && (
            <button
              onClick={() => { setSelectedSubject(''); setResources([]); }}
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              Clear subject
            </button>
          )}
        </div>
      </div>

      {/* Subject Selector */}
      {subjects.length > 0 && (
        <div className="mb-6">
          {/* Show search input for grades with many subjects */}
          {subjects.length > 10 && (
            <div className="mb-3">
              <div className="relative max-w-xs">
                <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input
                  type="text"
                  placeholder="Search subjects..."
                  value={subjectSearch}
                  onChange={(e) => setSubjectSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {filteredSubjects.map((s) => (
              <button
                key={s}
                onClick={() => { setSelectedSubject(s); setSubjectSearch(''); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-100 ${
                  selectedSubject === s
                    ? 'bg-brand-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-brand-50 hover:text-brand-700'
                }`}
              >
                {s}
              </button>
            ))}
            {subjects.length > 10 && filteredSubjects.length === 0 && (
              <p className="text-sm text-gray-400 py-1">No subjects matching "{subjectSearch}"</p>
            )}
          </div>
        </div>
      )}

      {/* Resources Section */}
      <div className="w-full">
        {!selectedSubject && subjects.length > 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gray-100 text-gray-400 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
            </div>
            <p className="text-gray-500 text-sm">Select a subject above to browse resources</p>
          </div>
        )}

        {selectedSubject && (
          <>
            {loading && (
              <Spinner text="Loading resources…" className="py-16" />
            )}
            {error && (
              <div className="text-center text-red-600 bg-red-50 rounded-xl border border-red-100 p-6 text-sm">{error}</div>
            )}
            {!loading && !error && resources.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gray-100 text-gray-400 mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                </div>
                <p className="text-gray-500 text-sm">No resources found for <span className="font-medium">{selectedSubject}</span></p>
              </div>
            )}

            {!loading && !error && resources.length > 0 && (
              <CreateTable
                resources={resources}
                collectionOne={collectionOne}
                mainDoc={cleanGrade}
                CollectionTwo={toPascalCase(selectedSubject)}
                grade={cleanGrade}
              />
            )}
          </>
        )}
      </div>

      {!hasGrade && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-gray-500 text-sm">Grade information was not provided.</p>
          <Link to="/" className="mt-2 text-brand-600 hover:text-brand-700 text-sm font-medium">← Return to homepage</Link>
        </div>
      )}
    </div>
  );
}

export default GradePage;
